import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { adminApi } from '@/lib/admin-api'
import { formatDate } from '@/lib/format'

const STATUSES = ['pending_email', 'pending_kyc', 'active', 'review', 'suspended', 'closed']

export function Users() {
  const { t, i18n } = useTranslation('admin')
  const navigate = useNavigate()
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: ['admin-users', status, search, page],
    queryFn: () =>
      adminApi.listUsers({
        ...(status ? { status } : {}),
        ...(search ? { search } : {}),
        page,
      }),
  })

  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.pageSize)) : 1

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('users.title')}</h1>

      <div className="flex flex-wrap items-end gap-3">
        <input
          type="search"
          className="clari-input max-w-xs"
          placeholder={t('users.search_placeholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          aria-label={t('users.search_placeholder')}
        />
        <select
          className="clari-input max-w-xs"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(1)
          }}
          aria-label={t('users.status_filter')}
        >
          <option value="">{t('common.all')}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`statuses.${s}`)}
            </option>
          ))}
        </select>
      </div>

      {query.isError ? <ErrorBanner message={apiErrorMessage(query.error)} /> : null}
      {query.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

      {query.data ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">{t('users.col_email')}</th>
                <th className="px-4 py-2 font-medium">{t('users.col_status')}</th>
                <th className="px-4 py-2 font-medium">{t('users.col_plan')}</th>
                <th className="px-4 py-2 font-medium">{t('users.col_kyc')}</th>
                <th className="px-4 py-2 font-medium">{t('users.col_created')}</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => navigate(`/users/${u.id}`)}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-bg-subtle"
                >
                  <td className="px-4 py-2 font-mono">{u.email}</td>
                  <td className="px-4 py-2">{t(`statuses.${u.status}`)}</td>
                  <td className="px-4 py-2 uppercase">{u.plan}</td>
                  <td className="px-4 py-2">{u.kyc_status}</td>
                  <td className="px-4 py-2 text-text-muted">
                    {formatDate(u.created_at, i18n.language)}
                  </td>
                </tr>
              ))}
              {query.data.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-text-muted">
                    {t('users.empty')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="flex items-center gap-3 text-sm">
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('common.prev')}
        </button>
        <span className="text-text-muted">{t('common.page', { page })}</span>
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  )
}
